/**
 * MOCK · ASSUMED (CT-5528) — the notes of a profile, their comments and the
 * drive an attachment is picked from.
 *
 * The real note endpoints exist (note CRUD, the agent note node, the SQL profile
 * lookup) but their contract was not shared with the frontend yet, so every
 * shape here is assumed and marked `TODO(CT-5528 / backend)` at the call site.
 * Reconcile at cutover.
 *
 * Coverage — each row exists to make one screen reachable:
 *   n-1  public, mine, links and a few comments      — the editable note
 *   n-2  private, mine                               — scope badge + edit
 *   n-3  public, someone else's                      — no Edit
 *   n-4  written by an agent (human in the loop)     — the agent as author
 *   n-5  public, mine, 32 comments + links           — the bounded thread
 *   n-6  public, mine, no comments                   — the empty thread
 *   n-7  private, someone else's                     — ABSENT from the list; a
 *                                                      direct fetch is a 403
 *   n-8  mine, but its fetch fails (500)             — the error state
 */

/** Who "I" am in the mock. The API says `can_edit`; the frontend never derives it. */
export const MOCK_CURRENT_USER = Object.freeze({ id: 'u-priya', name: 'Priya Nair', role: 'Account Executive' });

export const NOTE_FAILING_ID = 'n-8';

const DAY_MS = 24 * 60 * 60 * 1000;
const hoursAgo = (hours) => new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
const daysAgo = (days) => new Date(Date.now() - days * DAY_MS).toISOString();

const SAM = { id: 'u-sam', name: 'Sam Ortiz', role: 'Solutions Engineer' };
const AGENT = { id: 'agent-deal', name: 'Deal Concierge', role: 'Agent' };
const ME = MOCK_CURRENT_USER;

const link = (id, label, url, kind = 'url') => ({ id, label, url, kind });

const buildNotes = () => [
  {
    id: 'n-1',
    entity_type: 'customers',
    entity_id: 'c-1',
    title: 'Renewal strategy for FY27',
    body:
      '<p>Sandra owns the budget line and the renewal closes in <strong>19 days</strong>. ' +
      'The governance addendum is the one blocker on our side.</p><ul><li>Send the migration timeline in writing</li>' +
      '<li>Introduce the executive sponsor</li></ul>',
    summary: 'Renewal closes in 19 days; the governance addendum is the only open blocker.',
    scope: 'public',
    author: ME,
    created_at: hoursAgo(3),
    edited_at: hoursAgo(1),
    links: [
      link('l-1', 'Governance addendum.pdf', 'drive://doc-1', 'document'),
      link('l-2', 'FY27 renewal plan', 'https://example.com/plans/fy27'),
    ],
  },
  {
    id: 'n-2',
    entity_type: 'customers',
    entity_id: 'c-1',
    title: 'Pricing guardrails (private)',
    body: '<p>Do not go below the 12% floor without finance sign-off.</p>',
    summary: 'Floor discount is 12%; anything lower needs finance.',
    scope: 'private',
    author: ME,
    created_at: daysAgo(2),
    edited_at: null,
    links: [],
  },
  {
    id: 'n-3',
    entity_type: 'customers',
    entity_id: 'c-1',
    title: 'Security questionnaire status',
    body: '<p>Sent the questionnaire on Monday; two sections are waiting on their CISO.</p>',
    summary: 'Two sections pending on their CISO.',
    scope: 'public',
    author: SAM,
    created_at: daysAgo(5),
    edited_at: null,
    links: [link('l-3', 'Security questionnaire.xlsx', 'drive://doc-2', 'document')],
  },
  {
    id: 'n-4',
    entity_type: 'customers',
    entity_id: 'c-1',
    title: 'Snapshot reviewed by Deal Concierge',
    body: '<p>Re-verified contact fields against the CRM sync. No conflicts found.</p>',
    summary: 'Contact fields re-verified; no conflicts.',
    scope: 'public',
    author: AGENT,
    created_at: daysAgo(9),
    edited_at: null,
    links: [],
  },
  {
    id: 'n-5',
    entity_type: 'customers',
    entity_id: 'c-1',
    title: 'QBR follow-ups',
    body: '<p>Action items from the quarterly business review, with owners and dates.</p>',
    summary: null,
    scope: 'public',
    author: ME,
    created_at: daysAgo(40),
    edited_at: daysAgo(39),
    links: [
      link('l-4', 'QBR deck', 'https://example.com/qbr'),
      link('l-5', 'Action items.docx', 'drive://doc-3', 'document'),
    ],
  },
  {
    id: 'n-6',
    entity_type: 'customers',
    entity_id: 'c-1',
    title: 'Intro call notes',
    body: '<p>First call went well. She prefers email over calls.</p>',
    summary: 'Prefers email over calls.',
    scope: 'public',
    author: ME,
    created_at: daysAgo(75),
    edited_at: null,
    links: [],
  },
  {
    id: 'n-7',
    entity_type: 'customers',
    entity_id: 'c-1',
    title: 'Sam’s private thoughts',
    body: '<p>Not for sharing.</p>',
    summary: null,
    scope: 'private',
    author: SAM,
    created_at: daysAgo(12),
    edited_at: null,
    links: [],
  },
  {
    id: NOTE_FAILING_ID,
    entity_type: 'customers',
    entity_id: 'c-1',
    title: 'A note whose preview fails to load',
    body: '<p>You will not see this.</p>',
    summary: 'Open it to see the error state.',
    scope: 'public',
    author: ME,
    created_at: daysAgo(100),
    edited_at: null,
    links: [],
  },
];

const buildComments = () => ({
  'n-1': [
    { id: 'c-n1-1', text: 'Can we get legal to look at the addendum today?', author: SAM, created_at: hoursAgo(2) },
    { id: 'c-n1-2', text: 'Already in their queue — I will chase it.', author: ME, created_at: hoursAgo(1) },
  ],
  // Enough to overflow the thread and need a second page.
  'n-5': Array.from({ length: 32 }, (_, idx) => ({
    id: `c-n5-${idx + 1}`,
    text: `Follow-up ${idx + 1}: ${idx % 2 === 0 ? 'owner confirmed' : 'waiting on the customer'}.`,
    author: idx % 3 === 0 ? SAM : ME,
    created_at: hoursAgo(32 - idx),
  })),
});

const buildDrives = () => ({
  drives: [{ id: 'vault-1', name: 'Customer files' }],
  root: {
    'vault-1': {
      folders: [{ folder_uuid: 'folder-contracts', name: 'Contracts' }],
      documents: [{ document_uuid: 'doc-1', name: 'Governance addendum.pdf' }],
    },
  },
  folders: {
    'folder-contracts': {
      folders: [],
      documents: [
        { document_uuid: 'doc-2', name: 'Security questionnaire.xlsx' },
        { document_uuid: 'doc-3', name: 'Action items.docx' },
      ],
    },
  },
});

const buildSeed = () => ({
  notes: buildNotes(),
  comments: buildComments(),
  drive: buildDrives(),
  sequence: 100,
});

let mockNotes = buildSeed();

export const getMockNotes = () => mockNotes.notes;
export const getMockNoteComments = () => mockNotes.comments;
export const getMockDrive = () => mockNotes.drive;

/** Ids for records the mock creates, so two creates never collide. */
export const nextMockId = (prefix) => {
  mockNotes.sequence += 1;
  return `${prefix}-${mockNotes.sequence}`;
};

/** Test-only: reset the mutable seed between tests within a file. */
export const __resetMockNotesForTests = () => {
  mockNotes = buildSeed();
};

/** A private note is visible to its author only; the backend decides, never the client. */
export const canSeeMockNote = (note) => note.scope !== 'private' || note.author.id === MOCK_CURRENT_USER.id;

/** `can_edit` is the API's call: the owner, and nobody else — an agent's note is not editable by me. */
export const canEditMockNote = (note) => note.author.id === MOCK_CURRENT_USER.id;

/** Block ends become a space; inline tags (bold, links) vanish, so "<b>19 days</b>." reads "19 days." */
const stripTags = (html) =>
  html
    .replace(/<\/(p|li|h[1-6]|div|ul|ol)>|<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();

/** A note as one row of the Activity feed (`quick_filter: 'notes'`). */
export const toActivityRow = (note) => ({
  id: note.id,
  kind: 'note',
  title: note.title,
  preview: stripTags(note.body),
  ai_summary: note.summary,
  occurred_at: note.created_at,
  author: note.author,
  scope: note.scope,
  can_edit: canEditMockNote(note),
});

/** The notes of one record that the current user may see, as feed rows. */
export const listMockNoteRows = (entityType, entityId) =>
  getMockNotes()
    .filter((note) => note.entity_type === entityType && note.entity_id === entityId)
    .filter(canSeeMockNote)
    .map(toActivityRow);

// ── What the transport answers with ──────────────────────────────────────────
//
// Each helper returns `{ body, meta? }` or `{ error: { status, description } }`, so
// the transport only wraps the result in an envelope and the rules stay here.

const fail = (status, description) => ({ error: { status, description } });

const findNote = (noteId) => getMockNotes().find((note) => note.id === noteId);

const SCOPES = ['public', 'private'];

/** Attachment labels come from the drive, which is the only place a name lives. */
const documentName = (documentId) => {
  const { root, folders } = getMockDrive();
  const documents = [
    ...Object.values(root).flatMap((entry) => entry.documents),
    ...Object.values(folders).flatMap((entry) => entry.documents),
  ];
  return documents.find((doc) => doc.document_uuid === documentId)?.name ?? documentId;
};

const buildLinks = (attachments = [], urls = []) => [
  ...attachments.map((attachment, index) =>
    link(
      `link-doc-${attachment.drive_file_id}-${index}`,
      documentName(attachment.drive_file_id),
      `drive://${attachment.drive_file_id}`,
      'document',
    ),
  ),
  ...urls.map((url, index) => link(`link-url-${index}`, url, url)),
];

const validate = ({ title, body, scope }) => {
  if (!title?.trim() || !stripTags(body ?? '')) return fail(422, 'A note needs a title and a body.');
  if (!SCOPES.includes(scope)) return fail(422, 'Scope must be public or private.');
  return null;
};

export const getMockNoteDetail = (noteId) => {
  const note = findNote(noteId);
  if (!note) return fail(404, `No note ${noteId}`);
  if (noteId === NOTE_FAILING_ID) return fail(500, 'Mock note failure.');
  if (!canSeeMockNote(note)) return fail(403, 'No access to this note');

  return {
    body: {
      ...toActivityRow(note),
      body: note.body,
      created_at: note.created_at,
      edited_at: note.edited_at,
      links: note.links,
    },
  };
};

export const createMockNote = ({ entity_type: entityType, entity_id: entityId, title, body, scope = 'public', attachments, urls } = {}) => {
  const invalid = validate({ title, body, scope });
  if (invalid) return invalid;

  const note = {
    id: nextMockId('n'),
    entity_type: entityType,
    entity_id: entityId,
    title: title.trim(),
    body,
    summary: null,
    scope,
    author: MOCK_CURRENT_USER,
    created_at: new Date().toISOString(),
    edited_at: null,
    links: buildLinks(attachments, urls),
  };
  // Newest first, like the feed: the new note is the first thing the list shows.
  getMockNotes().unshift(note);
  return { body: { note_id: note.id } };
};

export const updateMockNote = ({ note_id: noteId, title, body, scope, attachments, urls } = {}) => {
  const note = findNote(noteId);
  if (!note) return fail(404, `No note ${noteId}`);
  if (!canEditMockNote(note)) return fail(403, 'Only the author can edit this note');

  const invalid = validate({ title, body, scope: scope ?? note.scope });
  if (invalid) return invalid;

  Object.assign(note, {
    title: title.trim(),
    body,
    scope: scope ?? note.scope,
    edited_at: new Date().toISOString(),
    links: buildLinks(attachments, urls),
  });
  return { body: { note_id: note.id } };
};

/** Newest first per page; the hook lays a thread out oldest-to-newest. */
export const listMockComments = ({ note_id: noteId, page = 1, page_size: pageSize = 10 } = {}) => {
  const note = findNote(noteId);
  if (!note) return fail(404, `No note ${noteId}`);
  if (!canSeeMockNote(note)) return fail(403, 'No access to this note');

  const all = [...(getMockNoteComments()[noteId] ?? [])].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at),
  );
  const start = (page - 1) * pageSize;
  const items = all.slice(start, start + pageSize);

  return {
    body: { items },
    meta: {
      total_items: all.length,
      total_pages: Math.max(1, Math.ceil(all.length / pageSize)),
      current_page: page,
      per_page: pageSize,
      has_next: start + items.length < all.length,
      has_previous: page > 1,
    },
  };
};

export const addMockComment = ({ note_id: noteId, text } = {}) => {
  const note = findNote(noteId);
  if (!note) return fail(404, `No note ${noteId}`);
  if (!canSeeMockNote(note)) return fail(403, 'No access to this note');
  if (!text?.trim()) return fail(422, 'A comment cannot be empty.');

  const comment = {
    id: nextMockId('c-n'),
    text: text.trim(),
    author: MOCK_CURRENT_USER,
    created_at: new Date().toISOString(),
  };
  const thread = getMockNoteComments();
  thread[noteId] = [...(thread[noteId] ?? []), comment];
  return { body: { comment_id: comment.id } };
};
